import { Target, Eye, Scale, ShieldCheck, Globe2, HeartHandshake } from "lucide-react";
import { FadeIn, Stagger, StaggerItem } from "../motion";
import { ABOUT_PARAS, MISSION, VISION, VALUES, CONDUCT, ANTI_CORRUPTION, SOURCE_COUNTRIES, SERVE_REGIONS, IMAGES } from "../../data/content";

export const MAP_MARKERS = [
  { name: "India", x: 69, y: 52, type: "source" }, { name: "Nepal", x: 71.5, y: 46, type: "source" },
  { name: "Bangladesh", x: 73.5, y: 49.5, type: "source" }, { name: "Sri Lanka", x: 70.5, y: 60, type: "source" },
  { name: "Thailand", x: 76.5, y: 54, type: "source" }, { name: "Africa", x: 58, y: 66, type: "source" },
  { name: "Qatar", x: 62, y: 46, type: "serve" }, { name: "Kuwait", x: 61, y: 42.5, type: "serve" },
  { name: "Oman", x: 64, y: 50, type: "serve" }, { name: "Bahrain", x: 61.5, y: 44.5, type: "serve" },
  { name: "Saudi Arabia", x: 59.5, y: 47.5, type: "serve" }, { name: "Eastern Europe", x: 56, y: 28, type: "serve" },
  { name: "St. Lucia", x: 31.5, y: 55, type: "serve" }, { name: "Guyana", x: 30.5, y: 61, type: "serve" },
  { name: "Dominica", x: 31, y: 52.5, type: "serve" },
];

export const AboutStory = () => (
  <section className="bg-[#FAF7F2]" data-testid="about-story-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-[1fr_0.8fr] gap-14 items-start">
      <FadeIn><p className="font-mono text-xs tracking-[0.3em] uppercase text-[#811F5D] mb-5">Our Story</p><div className="space-y-5 text-[#4B3A45] leading-relaxed">{ABOUT_PARAS.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</div></FadeIn>
      <FadeIn delay={0.15} className="lg:sticky lg:top-24"><div className="relative"><div className="border-2 border-[#D4AF37]/60 absolute -top-4 -right-4 left-8 bottom-8" /><img src={IMAGES.interview} alt="Vardhamaan interview session" className="relative w-full h-[380px] object-cover" data-testid="about-story-image" /></div></FadeIn>
    </div>
  </section>
);

export const PurposeSection = () => (
  <section className="bg-[#4A1136] relative overflow-hidden" data-testid="mission-vision-section">
    <div className="absolute inset-0 hero-pattern opacity-30 pointer-events-none" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 relative z-10">
      <Stagger className="grid md:grid-cols-3 gap-8">
        {[{ icon: Target, title: "Our Mission", text: MISSION }, { icon: Eye, title: "Our Vision", text: VISION }, { icon: Scale, title: "Our Values", text: VALUES }].map(({ icon: Icon, title, text }) => (
          <StaggerItem
            key={title}
            className="rounded-2xl border border-[#D4AF37]/30 p-8 bg-[#3A0D2A]/75 backdrop-blur-md transition-all duration-300 hover:bg-[#3A0D2A]/90 hover:border-[#D4AF37] hover:-translate-y-1.5 shadow-xl flex flex-col justify-between"
            data-testid={`card-${title.toLowerCase().replaceAll(" ", "-")}`}
          >
            <div>
              <div className="w-14 h-14 rounded-2xl bg-white/5 border border-[#D4AF37]/30 flex items-center justify-center mb-6">
                <Icon size={28} className="text-[#D4AF37]" />
              </div>
              <h3 className="font-heading font-extrabold text-white text-2xl mb-3 tracking-tight">{title}</h3>
              <p className="text-sm text-white/75 leading-relaxed font-normal">{text}</p>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

const ReachMap = () => (
  <FadeIn className="relative bg-[#3A0D2A] p-6 sm:p-10 mb-10 overflow-hidden rounded-3xl border border-[#D4AF37]/25 shadow-2xl" data-testid="global-reach-map">
    <div className="absolute inset-0 hero-pattern opacity-30 pointer-events-none" />
    <div className="relative">
      <img src="/assets/world-map-gold.png" alt="World map" className="w-full opacity-60" />
      {MAP_MARKERS.map((marker) => (
        <div
          key={marker.name}
          className="absolute group -translate-x-1/2 -translate-y-1/2 cursor-pointer"
          style={{ left: `${marker.x}%`, top: `${marker.y}%` }}
          data-testid={`map-marker-${marker.name.toLowerCase().replaceAll(" ", "-").replaceAll(".", "")}`}
        >
          <span className={`map-dot ${marker.type === "source" ? "map-dot-source" : "map-dot-serve"}`} />
          <span className="map-tooltip">{marker.name}</span>
        </div>
      ))}
    </div>
    <div className="relative flex flex-wrap gap-x-8 gap-y-3 mt-6 justify-center pt-4 border-t border-white/10">
      <span className="flex items-center gap-2 text-xs sm:text-sm text-white/80 font-medium">
        <span className="map-dot map-dot-source !relative !animate-none" /> Candidates We Source From
      </span>
      <span className="flex items-center gap-2 text-xs sm:text-sm text-white/80 font-medium">
        <span className="map-dot map-dot-serve !relative !animate-none" /> Clients We Serve
      </span>
    </div>
  </FadeIn>
);

const ReachCards = () => (
  <div className="grid md:grid-cols-2 gap-8">
    <FadeIn className="border border-[#811F5D]/15 p-8 sm:p-10 rounded-2xl bg-white shadow-sm hover:shadow-xl transition-all duration-300" data-testid="source-countries-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#FAF7F2] flex items-center justify-center text-[#811F5D]">
          <HeartHandshake size={22} />
        </div>
        <h3 className="font-heading font-extrabold text-[#2A0E20] text-lg uppercase tracking-wide">Candidates We Source From</h3>
      </div>
      <div className="flex flex-wrap gap-2.5">
        {SOURCE_COUNTRIES.map((country) => (
          <span key={country} className="bg-[#811F5D] text-white text-xs sm:text-sm px-4 py-2 font-semibold transition-transform duration-200 hover:scale-[1.05] active:scale-95 cursor-default select-none shadow-sm rounded-xl">
            {country}
          </span>
        ))}
      </div>
      <p className="mt-6 text-sm text-[#6B5A64] leading-relaxed">
        Strong associate partnerships built over the past two decades, supported by our Pan-India recruitment offices and vetted training centers.
      </p>
    </FadeIn>
    <FadeIn delay={0.12} className="border border-[#811F5D]/15 p-8 sm:p-10 rounded-2xl bg-white shadow-sm hover:shadow-xl transition-all duration-300" data-testid="serve-regions-card">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-[#FAF7F2] flex items-center justify-center text-[#D4AF37]">
          <Globe2 size={22} />
        </div>
        <h3 className="font-heading font-extrabold text-[#2A0E20] text-lg uppercase tracking-wide">Clients We Serve</h3>
      </div>
      <div className="flex flex-wrap gap-2.5">
        {SERVE_REGIONS.map((region) => (
          <span key={region} className="bg-[#D4AF37] text-[#4A1136] text-xs sm:text-sm px-4 py-2 font-bold transition-transform duration-200 hover:scale-[1.05] active:scale-95 cursor-default select-none shadow-sm rounded-xl">
            {region}
          </span>
        ))}
      </div>
      <p className="mt-6 text-sm text-[#6B5A64] leading-relaxed">
        Trusted workforce mobilization across the Middle East (Qatar, UAE, Kuwait, Saudi Arabia, Bahrain, Oman), Eastern Europe, South Asia, and the Caribbean.
      </p>
    </FadeIn>
  </div>
);

export const GlobalReachSection = () => (
  <section className="bg-white" data-testid="global-reach-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
      <FadeIn className="mb-12">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#811F5D] font-bold mb-3">Our Global Reach</p>
        <h2 className="font-heading font-extrabold text-[#2A0E20] text-2xl sm:text-3xl lg:text-4xl tracking-tight">
          Sourcing from six regions.<br />Serving four continents.
        </h2>
      </FadeIn>
      <ReachMap />
      <ReachCards />
    </div>
  </section>
);

const EthicsList = ({ items, accent }) => (
  <ul className="space-y-3.5">
    {items.map((item) => (
      <li key={item} className="flex gap-3 text-sm text-[#4B3A45] leading-relaxed">
        <span className={`${accent} font-bold shrink-0 mt-0.5`}>◆</span>
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export const EthicsSection = () => (
  <section className="bg-[#FAF7F2] border-t border-[#811F5D]/10" data-testid="ethics-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
      <FadeIn className="mb-12 max-w-2xl">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#811F5D] font-bold mb-3">Governance & Ethics</p>
        <h2 className="font-heading font-extrabold text-[#2A0E20] text-2xl sm:text-3xl lg:text-4xl tracking-tight">
          Ethics, integrity, and professionalism — non-negotiable.
        </h2>
      </FadeIn>
      <div className="grid md:grid-cols-2 gap-8">
        <FadeIn className="bg-white p-8 sm:p-10 rounded-2xl border-t-4 border-[#811F5D] border-x border-b border-[#811F5D]/10 shadow-md hover:shadow-xl transition-all duration-300" data-testid="code-of-conduct-card">
          <h3 className="font-heading font-extrabold text-[#2A0E20] text-xl uppercase tracking-wide mb-6">Code of Conduct</h3>
          <EthicsList items={CONDUCT} accent="text-[#D4AF37]" />
        </FadeIn>
        <FadeIn delay={0.12} className="bg-white p-8 sm:p-10 rounded-2xl border-t-4 border-[#D4AF37] border-x border-b border-[#811F5D]/10 shadow-md hover:shadow-xl transition-all duration-300" data-testid="anti-corruption-card">
          <div className="flex items-center gap-3 mb-6">
            <ShieldCheck size={24} className="text-[#811F5D]" />
            <h3 className="font-heading font-extrabold text-[#2A0E20] text-xl uppercase tracking-wide">Anti-Corruption Policy</h3>
          </div>
          <EthicsList items={ANTI_CORRUPTION} accent="text-[#811F5D]" />
        </FadeIn>
      </div>
    </div>
  </section>
);